/** @format */

import {useState, ReactNode} from 'react';

import {Box, Typography, CircularProgress} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import Snackbar from 'components/Snackbar';
import {AiMonitoringConfig, CampaignSelectionMode} from 'graphql/generated/types';

import {
  StyledOpenButton,
  ChecklistIcon,
  SelectedChipsContainer,
  StyledChip,
  RemainingCountText,
  OpenModalContainer,
} from 'components/BaseSearchModal/styles';

import {TOOLTIP_TEXTS} from '../Form/constants';
import FieldLabelWithHelp from '../Form/components/FieldLabelWithHelp';
import {NeedToConnectMetrikaBadge, GoToSettingButton, FormContainer} from '../Form/styles';
import CampaignsMonitoringModal from '../CampaignsMonitoringModal';
import KpiField from '../SharedFields/KpiField';
import GoalsField from '../SharedFields/GoalsField';
import AttributionModelField from '../SharedFields/AttributionModelField';
import BudgetField from '../SharedFields/BudgetField';
import {FormDataOptions} from '../../types';
import {
  StatusBadge,
  MonitoringGridContainer,
  ButtonsContainer,
  MonitoringSubmitButton,
  StopButton,
} from './styles';
import {useMonitoringConfigForm, MonitoringFormState} from './hooks/useMonitoringConfigForm';

interface MonitoringFormProps {
  config: AiMonitoringConfig | null;
  formDataOptions: FormDataOptions;
  onStart: (formState: MonitoringFormState) => Promise<void>;
  onStop: () => Promise<void>;
  onUpdate: (formState: MonitoringFormState) => Promise<void>;
  mutationLoading: boolean;
  hasReports: boolean;
  needToConnectMetrika: boolean;
  onGoToSettings: () => void;
}

const MonitoringForm = ({
  config,
  formDataOptions,
  onStart,
  onStop,
  onUpdate,
  mutationLoading,
  hasReports,
  needToConnectMetrika,
  onGoToSettings,
}: MonitoringFormProps) => {
  const {t} = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<ReactNode>('');
  const isActive = config?.status === 'ACTIVE';

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);

  const {
    state,
    isDirty,
    isValid,
    handlers: {
      handleCampaignChange,
      handleGoalChange,
      handleKpiChange,
      handleKpiAmountChange,
      handleAttributionChange,
      handleBudgetChange,
      handleCampaignSelectionModeChange,
      handleCampaignNameMaskChange,
    },
  } = useMonitoringConfigForm(config);

  const {
    campaigns,
    goals,
    kpi,
    kpiAmount,
    attributionModel,
    budget,
    campaignSelectionMode,
    campaignNameMask,
  } = state;
  const {goalOptions, campaignOptions} = formDataOptions;

  const handleCampaignModalConfirm = (result: {
    mode: CampaignSelectionMode;
    campaignIds: string[];
    mask: string;
  }) => {
    handleCampaignSelectionModeChange(result.mode);
    if (result.mode === 'AUTO') {
      handleCampaignNameMaskChange(result.mask);
      handleCampaignChange([]);
    } else {
      handleCampaignChange(result.campaignIds);
    }
  };

  const showSnackbar = (message: ReactNode) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const withSnackbar = async (action: () => Promise<void>, successMessage: ReactNode) => {
    try {
      await action();
      showSnackbar(successMessage);
    } catch {
      showSnackbar(<Trans>Произошла ошибка. Попробуйте еще раз</Trans>);
    }
  };

  const handleStart = () =>
    withSnackbar(
      () => onStart(state),
      <>
        <b>
          <Trans>Мониторинг запущен!</Trans>
        </b>
        <br />
        <Trans>Отчёты будут формироваться 1-го числа каждого месяца.</Trans>
      </>,
    );

  const handleStop = () => withSnackbar(() => onStop(), <Trans>Мониторинг остановлен</Trans>);

  const handleUpdate = () =>
    withSnackbar(() => onUpdate(state), <Trans>Настройки мониторинга обновлены</Trans>);

  return (
    <FormContainer>
      <Box mb={2}>
        <StatusBadge isActive={isActive}>
          <Box component='span' className='material-symbols-outlined' style={{fontSize: 16}}>
            {isActive ? 'toggle_on' : 'toggle_off'}
          </Box>
          {isActive ? <Trans>Активен</Trans> : <Trans>Не запущен</Trans>}
        </StatusBadge>
      </Box>

      <Typography
        variant='body2'
        sx={{color: theme => theme.palette.semantic_dark?.surface.onSurface, mb: 2}}
      >
        {(() => {
          if (isActive) {
            return <Trans>Вы можете изменить настройки или остановить мониторинг</Trans>;
          }
          if (hasReports) {
            return <Trans>Запустите мониторинг и получите отчет 1-го числа</Trans>;
          }
          return <Trans>Задайте правила бесплатного ежемесячного мониторинга</Trans>;
        })()}
      </Typography>

      <MonitoringGridContainer>
        <Box>
          <FieldLabelWithHelp tooltipText={TOOLTIP_TEXTS.CAMPAIGNS}>
            <Trans>Кампании</Trans>
            {needToConnectMetrika && (
              <NeedToConnectMetrikaBadge>
                <Trans>Метрика не подключена</Trans>
              </NeedToConnectMetrikaBadge>
            )}
          </FieldLabelWithHelp>
          {needToConnectMetrika ? (
            <GoToSettingButton onClick={onGoToSettings}>
              <Trans>Перейти в настройки</Trans>
            </GoToSettingButton>
          ) : (
            <>
              <OpenModalContainer>
                <StyledOpenButton onClick={() => setCampaignModalOpen(true)}>
                  <ChecklistIcon />
                  <Trans>Выбрать</Trans>
                </StyledOpenButton>
                {campaignSelectionMode === 'MANUAL' &&
                  campaigns.length > 0 &&
                  t('{{selected}} из {{total}}', {
                    selected: campaigns.length,
                    total: campaignOptions.length,
                  })}
              </OpenModalContainer>
              {campaignSelectionMode === 'AUTO' && campaignNameMask ? (
                <Box sx={{mt: 1}}>
                  <Box
                    sx={{
                      fontSize: '12px',
                      color: theme => theme.palette.semantic_dark?.surface.onSurfaceLight,
                      mb: 0.5,
                    }}
                  >
                    <Trans>Автоматический режим по маске</Trans>
                  </Box>
                  <StyledChip
                    icon={
                      <Box
                        component='span'
                        className='material-symbols-outlined'
                        style={{fontSize: 14, color: 'inherit'}}
                      >
                        filter_alt
                      </Box>
                    }
                    label={campaignNameMask}
                    size='small'
                    title={campaignNameMask}
                  />
                </Box>
              ) : campaigns.length > 0 && campaignSelectionMode === 'MANUAL' ? (
                <SelectedChipsContainer>
                  {campaigns.slice(0, 2).map(id => {
                    const option = campaignOptions.find(c => String(c.id) === id);
                    const label = option ? String(option.campaignName || option.id) : id;
                    return <StyledChip key={id} label={label} size='small' title={label} />;
                  })}
                  {campaigns.length > 2 && (
                    <RemainingCountText>
                      {t('и ещё {{count}}', {count: campaigns.length - 2})}
                    </RemainingCountText>
                  )}
                </SelectedChipsContainer>
              ) : null}
              <CampaignsMonitoringModal
                open={campaignModalOpen}
                onClose={() => setCampaignModalOpen(false)}
                campaigns={campaignOptions}
                initialMode={campaignSelectionMode}
                initialMask={campaignNameMask}
                initialSelectedIds={campaigns}
                onConfirm={handleCampaignModalConfirm}
              />
            </>
          )}
        </Box>

        <KpiField
          kpi={kpi}
          kpiAmount={kpiAmount}
          onKpiChange={handleKpiChange}
          onKpiAmountChange={handleKpiAmountChange}
          disabled={needToConnectMetrika}
        />

        <GoalsField
          goals={goals}
          goalOptions={goalOptions}
          onGoalChange={handleGoalChange}
          disabled={needToConnectMetrika}
        />

        <BudgetField
          budget={budget}
          onBudgetChange={handleBudgetChange}
          disabled={needToConnectMetrika}
        />

        <AttributionModelField
          attributionModel={attributionModel}
          onAttributionChange={handleAttributionChange}
          disabled={needToConnectMetrika}
        />
      </MonitoringGridContainer>

      <ButtonsContainer>
        {(() => {
          const spinner = <CircularProgress size={16} sx={{color: 'inherit'}} />;

          if (isActive) {
            return (
              <>
                <StopButton
                  onClick={handleStop}
                  disabled={mutationLoading}
                  sx={!isDirty ? {width: '100%'} : undefined}
                >
                  {mutationLoading ? spinner : <Trans>Остановить мониторинг</Trans>}
                </StopButton>
                {isDirty && (
                  <MonitoringSubmitButton
                    onClick={handleUpdate}
                    disabled={mutationLoading || !isValid}
                  >
                    {mutationLoading ? (
                      spinner
                    ) : (
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Box
                          component='span'
                          className='material-symbols-outlined'
                          style={{fontSize: 18}}
                        >
                          refresh
                        </Box>
                        <Trans>Обновить настройки</Trans>
                      </Box>
                    )}
                  </MonitoringSubmitButton>
                )}
              </>
            );
          }

          const startLabel = isValid ? (
            <Trans>Запустить мониторинг</Trans>
          ) : (
            <Trans>Заполните поля для запуска</Trans>
          );

          return (
            <MonitoringSubmitButton
              onClick={handleStart}
              disabled={!isValid || mutationLoading}
              sx={{width: '100%'}}
            >
              {mutationLoading ? spinner : startLabel}
            </MonitoringSubmitButton>
          );
        })()}
      </ButtonsContainer>

      <Snackbar
        isSnackbarOpen={snackbarOpen}
        handleSnackbarClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        vertical='bottom'
        horizontal='left'
      />
    </FormContainer>
  );
};

export default MonitoringForm;
